/**
 * Klaret iconography — MingCute (Apache-2.0) is the ONLY icon set (handoff §8).
 * line = available, fill = active; AI features use the quill; never Lucide/
 * Heroicons/emoji. Icons are monochrome `currentColor`, 24×24 grid, sized in
 * multiples of 4px (16 inline / 20 default UI / 24 page-header).
 *
 * This module is the single swap-surface: kit components import glyphs from here
 * (semantic aliases) so a glyph choice changes in one place. Components may also
 * import additional MingCute glyphs directly from '@mingcute/react' using the
 * `<Name>Line` / `<Name>Fill` convention when a one-off is needed.
 */
import type { ComponentType, SVGProps } from 'react';

// MingCute icon component signature (size + color + standard SVG props).
// `size` mirrors MingCute's own type (number | string); components are
// forwardRef exotics, so type the icon loosely as a ComponentType.
export type MgcIconProps = { size?: number | string; color?: string } & SVGProps<SVGSVGElement>;
export type MgcIcon = ComponentType<MgcIconProps>;

export {
  // structure / nav
  // MenuLine: the mobile navigation entry point. Re-exported because the admin app opens the
  // Settings-style menu screen from its own topbar and does not (and should not) take a direct
  // dependency on @mingcute/react just for one glyph.
  MenuLine,
  GridLine, GridFill,
  Group2Line, Group2Fill,
  Settings3Line, Settings3Fill,
  Settings1Line, Settings1Fill,
  NotificationLine, NotificationFill,
  NotificationNewdotLine, NotificationNewdotFill,
  ShieldLine, ShieldFill,
  ChartBarLine, ChartBarFill,
  FileCertificateLine, FileCertificateFill,
  Home3Line, Home3Fill,
  InboxLine, InboxFill,
  AlertLine, AlertFill,
  MailSendLine, MailSendFill,
  MailboxLine, MailboxFill,
  ListOrderedLine, ListOrderedFill,
  QuillPenLine, QuillPenFill,
  FilterLine, FilterFill,
  Book2Line, Book2Fill,
  Message3Line, Message3Fill,
  CalendarLine, CalendarFill,
  CalendarTimeAddLine, CalendarTimeAddFill,
  Calendar2Line, Calendar2Fill,
  LayoutLine, LayoutFill,
  CheckboxLine, CheckboxFill,
  User3Line, User3Fill,
  TargetLine, TargetFill,
  TableLine, TableFill,
  Table3Line, Table3Fill,
  UploadLine, UploadFill,
  // controls / affordances
  Search2Line, Search2Fill,
  CloseLine, CloseFill,
  CheckLine, CheckFill,
  EyeLine, EyeFill,
  EyeCloseLine,
  RightSmallLine, DownSmallLine, LeftSmallLine, UpSmallLine,
  RightLine, DownLine,
  More1Line, More2Line,
  AddLine,
  Copy2Line, Copy2Fill,
  // row actions — one glyph per verb so a row's controls read at a glance
  ArchiveLine, ArchiveFill,
  DeleteLine, DeleteFill,
  Edit2Line,
  // library folders (sequence library tree). Open/closed is the disclosure state, not availability,
  // so both the line pair AND FolderOpenLine are exported rather than reusing the line/fill idiom.
  FolderLine, FolderFill,
  FolderOpenLine,
  // status metaphors (handoff §8 reserved)
  AlertOctagonLine, AlertOctagonFill,
  WarningLine, WarningFill,
  InformationLine, InformationFill,
  CheckCircleLine, CheckCircleFill,
  // chrome
  SunLine, MoonLine,
  Translate2Line,
  ExitLine,
  LoadingLine,
  Refresh1Line,
  DownloadLine, DownloadFill,
} from '@mingcute/react';

import {
  GridLine as _GridLine, GridFill as _GridFill,
  Group2Line as _Group2Line, Group2Fill as _Group2Fill,
  Settings3Line as _Settings3Line, Settings3Fill as _Settings3Fill,
  NotificationLine as _NotificationLine, NotificationFill as _NotificationFill,
  ShieldLine as _ShieldLine, ShieldFill as _ShieldFill,
  ChartBarLine as _ChartBarLine, ChartBarFill as _ChartBarFill,
  FileCertificateLine as _FileCertificateLine, FileCertificateFill as _FileCertificateFill,
  Home3Line as _Home3Line, Home3Fill as _Home3Fill,
  InboxLine as _InboxLine, InboxFill as _InboxFill,
  AlertLine as _AlertLine, AlertFill as _AlertFill,
  MailSendLine as _MailSendLine, MailSendFill as _MailSendFill,
  MailboxLine as _MailboxLine, MailboxFill as _MailboxFill,
  ListOrderedLine as _ListOrderedLine, ListOrderedFill as _ListOrderedFill,
  QuillPenLine as _QuillPenLine, QuillPenFill as _QuillPenFill,
  FilterLine as _FilterLine, FilterFill as _FilterFill,
  Book2Line as _Book2Line, Book2Fill as _Book2Fill,
  Message3Line as _Message3Line, Message3Fill as _Message3Fill,
  CalendarLine as _CalendarLine, CalendarFill as _CalendarFill,
  CalendarTimeAddLine as _CalTimeAddLine, CalendarTimeAddFill as _CalTimeAddFill,
  LayoutLine as _LayoutLine, LayoutFill as _LayoutFill,
  CheckboxLine as _CheckboxLine, CheckboxFill as _CheckboxFill,
  User3Line as _User3Line, User3Fill as _User3Fill,
  TargetLine as _TargetLine, TargetFill as _TargetFill,
  TableLine as _TableLine, TableFill as _TableFill,
  UploadLine as _UploadLine, UploadFill as _UploadFill,
} from '@mingcute/react';

/** A line/fill glyph pair — line = available, fill = active (handoff §8). */
export interface NavGlyph {
  line: MgcIcon;
  fill: MgcIcon;
}

/**
 * Nav-icon registry keyed by NAVIGATION_MAP semantic names (the `mgc-*` glyphs
 * named in the shell + module nav fragments). The shell renders `.line` for
 * available items and `.fill` for the active one.
 */
export const NAV_ICONS = {
  workspaces: { line: _GridLine, fill: _GridFill },
  leadPool: { line: _Group2Line, fill: _Group2Fill },
  systemSettings: { line: _Settings3Line, fill: _Settings3Fill },
  attention: { line: _NotificationLine, fill: _NotificationFill },
  compliance: { line: _ShieldLine, fill: _ShieldFill },
  orgAnalytics: { line: _ChartBarLine, fill: _ChartBarFill },
  onboarding: { line: _FileCertificateLine, fill: _FileCertificateFill },
  overview: { line: _Home3Line, fill: _Home3Fill },
  inbox: { line: _InboxLine, fill: _InboxFill },
  attentionQueue: { line: _AlertLine, fill: _AlertFill },
  campaigns: { line: _MailSendLine, fill: _MailSendFill },
  sequences: { line: _ListOrderedLine, fill: _ListOrderedFill },
  templates: { line: _QuillPenLine, fill: _QuillPenFill },
  leadsIcps: { line: _FilterLine, fill: _FilterFill },
  emailsDomains: { line: _MailboxLine, fill: _MailboxFill },
  agents: { line: _QuillPenLine, fill: _QuillPenFill },
  knowledgeBase: { line: _Book2Line, fill: _Book2Fill },
  clientChat: { line: _Message3Line, fill: _Message3Fill },
  workspaceSettings: { line: _Settings3Line, fill: _Settings3Fill },
  calendars: { line: _CalendarLine, fill: _CalendarFill },
  // client portal
  dashboard: { line: _LayoutLine, fill: _LayoutFill },
  approvals: { line: _CheckboxLine, fill: _CheckboxFill },
  chat: { line: _Message3Line, fill: _Message3Fill },
  account: { line: _User3Line, fill: _User3Fill },
  myLeads: { line: _TargetLine, fill: _TargetFill },
  availability: { line: _CalTimeAddLine, fill: _CalTimeAddFill },
  columnRegistry: { line: _TableLine, fill: _TableFill },
  import: { line: _UploadLine, fill: _UploadFill },
} satisfies Record<string, NavGlyph>;

export type NavIconKey = keyof typeof NAV_ICONS;
